// src/pages/YTCampaignList.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL;
const YT_API_KEY = process.env.REACT_APP_YT_API_KEY;

// ── Language code → display name map ──
const LANGUAGE_LABEL = {
  hi: 'Hindi', ta: 'Tamil', te: 'Telugu', kn: 'Kannada',
  ml: 'Malayalam', bn: 'Bengali', gu: 'Gujarati', mr: 'Marathi',
  pa: 'Punjabi', ur: 'Urdu', en: 'English', es: 'Spanish',
  fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese',
  ru: 'Russian', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', ar: 'Arabic',
};

const DURATION_LABEL = {
  medium: 'Medium (4–20 min)',
  long: 'Long (> 20 min)',
};

const YTCampaignList = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Fetch all campaigns ──
  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/youtube_scrapper/list_campaign/`, {
        headers: { 'X-API-KEY': YT_API_KEY },
      });

      if (response.data.status === 'success') {
        setCampaigns(response.data.data || []);
      } else {
        toast.error('Failed to fetch campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error loading campaigns: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // ── Toggle active / inactive ──
  const handleToggleStatus = async (campaignId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    // Optimistic update
    setCampaigns(prev =>
      prev.map(c => c.id === campaignId ? { ...c, status: newStatus } : c)
    );

    try {
      const response = await axios.patch(
        `${BASE_URL}/youtube_scrapper/${campaignId}/toggle_campaign_status/`,
        { status: newStatus },
        { headers: { 'X-API-KEY': YT_API_KEY } }
      );

      if (response.data.status === 'success') {
        toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      } else {
        // Revert
        setCampaigns(prev =>
          prev.map(c => c.id === campaignId ? { ...c, status: currentStatus } : c)
        );
        toast.error('Failed to update status');
      }
    } catch (error) {
      // Revert
      setCampaigns(prev =>
        prev.map(c => c.id === campaignId ? { ...c, status: currentStatus } : c)
      );
      toast.error('Error updating status: ' + error.message);
    }
  };

  // ── Open detail modal ──
  const handleViewDetail = async (campaignId) => {
    setShowDetailModal(true);
    setDetailData(null);
    setDetailLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/youtube_scrapper/${campaignId}/campaign_detail/`,
        { headers: { 'X-API-KEY': YT_API_KEY } }
      );

      if (response.data.status === 'success') {
        setDetailData(response.data.data);
      } else {
        toast.error('Failed to load campaign details');
        setShowDetailModal(false);
      }
    } catch (error) {
      toast.error('Error loading details: ' + error.message);
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Progress bar helper ──
  const ProgressBar = ({ percentage }) => {
    const pct = Math.min(parseFloat(percentage) || 0, 100);
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  };

  // ── Status badge ──
  const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      status === 'active'
        ? 'bg-green-100 text-green-700'
        : status === 'inactive'
        ? 'bg-gray-100 text-gray-600'
        : 'bg-yellow-100 text-yellow-700'
    }`}>
      {status}
    </span>
  );

  // ── Format date ──
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2500,
          style: { background: '#363636', color: '#fff', borderRadius: '8px' },
        }}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">YouTube Campaigns</h1>
            <p className="text-sm text-gray-600 mt-1">Total: {campaigns.length} campaigns</p>
          </div>
          <button
            onClick={() => navigate('/yt/campaign/create')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm"
          >
            + New Campaign
          </button>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="mb-4 bg-white rounded-lg shadow p-4 flex justify-between items-center">
        <button
          onClick={fetchCampaigns}
          disabled={loading}
          className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
        >
          {loading ? '⟳ Loading...' : '↻ Refresh'}
        </button>
        <p className="text-xs text-gray-400">
          Campaigns run automatically via cron at 1:35 AM IST daily
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2" />
          <p className="text-gray-500">Loading campaigns...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    Campaign Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                    Progress
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Videos Fetched
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                        </svg>
                        <p className="font-medium">No campaigns found</p>
                        <p className="text-sm text-gray-400 mt-1">Create your first YouTube campaign to get started</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign, index) => {
                    const pct = parseFloat(campaign.progress_percentage) || 0;
                    return (
                      <tr key={campaign.id || index} className="hover:bg-gray-50 transition-colors">

                        {/* Campaign Name */}
                        <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] max-w-[200px]">
                          <span className="truncate block" title={campaign.name}>
                            {campaign.name}
                          </span>
                        </td>

                        {/* Status + Toggle */}
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleStatus(campaign.id, campaign.status)}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                campaign.status === 'active' ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                              role="switch"
                              aria-checked={campaign.status === 'active'}
                              title={`Click to ${campaign.status === 'active' ? 'deactivate' : 'activate'}`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  campaign.status === 'active' ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <StatusBadge status={campaign.status} />
                          </div>
                        </td>

                        {/* Progress */}
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="space-y-1">
                            <ProgressBar percentage={pct} />
                            <span className="text-xs text-gray-500">{pct.toFixed(1)}%</span>
                          </div>
                        </td>

                        {/* Videos Fetched */}
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                          {(campaign.total_videos_fetched || 0).toLocaleString()}
                        </td>

                        {/* Target */}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {(campaign.target_videos || 0).toLocaleString()}
                        </td>

                        {/* Created */}
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(campaign.created_at)}
                        </td>

                        {/* Completed */}
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {campaign.completed_at ? formatDate(campaign.completed_at) : '—'}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            {/* Detail button */}
                            <button
                              onClick={() => handleViewDetail(campaign.id)}
                              className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
                              title="View campaign details"
                            >
                              Detail
                            </button>
                            {/* Download button */}
                            <button
                              onClick={() => navigate(`/yt/campaign/download?id=${campaign.id}&name=${encodeURIComponent(campaign.name)}`)}
                              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                              title="Download CSV"
                            >
                              Download
                            </button>
                          </div>
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status Legend */}
      {!loading && (
        <div className="mt-4 p-4 bg-white rounded-lg shadow">
          <p className="text-sm font-medium text-gray-700 mb-2">Status Legend:</p>
          <div className="flex gap-6 text-sm">
            <span className="flex items-center gap-2">
              <div className="w-11 h-6 bg-green-500 rounded-full flex items-center">
                <div className="w-4 h-4 bg-white rounded-full ml-6" />
              </div>
              <span className="text-green-600 font-medium">Active</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-11 h-6 bg-gray-300 rounded-full flex items-center">
                <div className="w-4 h-4 bg-white rounded-full ml-1" />
              </div>
              <span className="text-gray-600 font-medium">Inactive</span>
            </span>
            <span className="text-gray-500 text-xs italic">Click toggle to change status</span>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {showDetailModal && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3" />
                <p className="text-gray-500 text-sm">Loading campaign details...</p>
              </div>
            ) : detailData ? (
              <>
                {/* ── Gradient Header ── */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-5 flex justify-between items-start">
                  <div>
                    <p className="text-blue-200 text-xs uppercase tracking-widest mb-1">Campaign Details</p>
                    <h3 className="text-xl font-bold text-white leading-tight">{detailData.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-2 ${
                      detailData.status === 'active'
                        ? 'bg-green-400 bg-opacity-20 text-green-100 border border-green-300 border-opacity-40'
                        : 'bg-gray-400 bg-opacity-20 text-gray-100 border border-gray-300 border-opacity-40'
                    }`}>
                      {detailData.status === 'active' ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 p-1.5 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* ── Body ── */}
                <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">

                  {/* Languages + Durations row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Languages */}
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest mb-3">
                        Languages
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(detailData.languages || []).map(code => (
                          <span key={code} className="px-3 py-1 bg-white text-blue-700 border border-blue-200 rounded-full text-xs font-semibold shadow-sm">
                            {LANGUAGE_LABEL[code] || code}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Duration Filters */}
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <p className="text-xs font-semibold text-purple-500 uppercase tracking-widest mb-3">
                        Duration Filters
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(detailData.durations || []).map(dur => (
                          <span key={dur} className="px-3 py-1 bg-white text-purple-700 border border-purple-200 rounded-full text-xs font-semibold shadow-sm">
                            {DURATION_LABEL[dur] || dur}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Signals */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800">Signals</p>
                        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                          {(detailData.keywords || []).length}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          const text = (detailData.keywords || []).join('\n');
                          navigator.clipboard.writeText(text);
                          toast.success('✓ All signals copied to clipboard!');
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy All
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {(detailData.keywords || []).map((kw, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 transition-colors group">
                          <span className="text-sm text-gray-700">{kw}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(kw);
                              toast.success('Copied!');
                            }}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-all p-1 rounded"
                            title="Copy this signal"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Excluded Channels */}
                  {detailData.excluded_channels && detailData.excluded_channels.length > 0 && (
                    <div className="bg-red-50 rounded-xl border border-red-100 overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-red-100 bg-white">
                        <p className="text-sm font-semibold text-gray-800">Excluded Channels</p>
                        <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                          {detailData.excluded_channels.length}
                        </span>
                      </div>
                      <div className="max-h-32 overflow-y-auto divide-y divide-red-50">
                        {detailData.excluded_channels.map((ch, i) => (
                          <div key={i} className="flex items-center gap-2 px-4 py-2.5">
                            <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            <span className="text-sm text-gray-700 break-all">
                              {typeof ch === 'string' ? ch : (ch.channel_name || ch.channel_url || ch.channel_id || JSON.stringify(ch))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Footer ── */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors text-gray-700 text-sm font-medium shadow-sm"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default YTCampaignList;